<template>
  <div class="renewal-page">
    <!-- Header -->
    <header class="bg-white shadow-sm">
      <div
        class="container py-2 py-md-3 d-flex align-items-center justify-content-center"
      >
        <img src="@/assets/logobaessh.svg" alt="BAESSH Logo" height="70" />
      </div>
    </header>
    <!-- Main -->
    <main class="container py-4 py-md-5">
      <div class="mb-4 mb-md-5 text-center text-md-left">
        <h1 class="display-title mb-2">Solicitud de Renovación</h1>
        <p class="text-muted mx-auto mx-md-0" style="max-width: 920px">
          Revise la información de su empresa y confirme los servicios que desea
          renovar para el próximo período. <br />
          Nota: Esta solicitud solo indica su intención de renovar los servicios
          próximos a vencer.
        </p>
      </div>

      <div v-if="isLoading" class="alert alert-info">
        Cargando datos del formulario...
      </div>
      <div v-else-if="loadError" class="alert alert-danger">
        {{ loadError }}
      </div>

      <div class="row" v-if="!loadError && !isLoading">
        <form @submit.prevent="submit">
          <div class="col-12">
            <!-- Datos del Cliente -->
            <section class="card shadow-sm mb-4">
              <div class="card-header d-flex align-items-center">
                <h3 class="h6 mb-0 d-flex align-items-center">
                  <span class="fa fa-building mr-2"></span>
                  <span class="font-weight-700">Cliente</span>
                </h3>
              </div>

              <div class="card-body">
                <div class="client-banner p-3 p-md-4 rounded mb-4">
                  <div class="row align-items-center">
                    <div class="col-md-6 mb-3 mb-md-0">
                      <div
                        class="text-uppercase small text-muted font-weight-600"
                      >
                        Empresa
                      </div>
                      <div class="h4 mb-0 font-weight-800">
                        {{ datos.orden.cotizacion_renovacion.cliente.nombre }}
                      </div>
                    </div>

                    <div class="col-md-6">
                      <div
                        class="text-uppercase small text-muted font-weight-600"
                      >
                        Folio de cotización anterior
                      </div>
                      <div class="font-mono font-weight-700">
                        {{ datos.orden.cotizacion_renovacion.folio }}
                      </div>
                    </div>
                  </div>
                </div>

                <div class="form-row">
                  <div class="form-group col-12 col-md-4">
                    <label class="font-weight-600" for="contact_name"
                      >Nombre de Contacto</label
                    >
                    <input
                      id="contact_name"
                      v-model.trim="
                        datos.orden.cotizacion_renovacion.cliente.contacto1
                      "
                      type="text"
                      class="form-control"
                      placeholder="Nombre completo"
                      required
                    />
                  </div>

                  <div class="form-group col-12 col-md-4">
                    <label class="font-weight-600" for="contact_email"
                      >Correo Electrónico</label
                    >
                    <input
                      id="contact_email"
                      v-model.trim="
                        datos.orden.cotizacion_renovacion.cliente.correo
                      "
                      type="email"
                      class="form-control"
                      placeholder="correo@ejemplo.com"
                      required
                    />
                  </div>

                  <div class="form-group col-12 col-md-4">
                    <label class="font-weight-600" for="contact_phone"
                      >Teléfono Móvil</label
                    >
                    <input
                      id="contact_phone"
                      v-model.trim="
                        datos.orden.cotizacion_renovacion.cliente.telefono
                      "
                      required
                      type="tel"
                      maxlength="10"
                      class="form-control"
                      placeholder="667 777 7777"
                    />
                  </div>
                </div>
              </div>
            </section>

            <!-- Resumen de servicios -->
            <section class="card shadow-sm mb-4">
              <div
                class="card-header d-flex align-items-center justify-content-between flex-wrap"
              >
                <h3 class="h6 mb-0 d-flex align-items-center">
                  <span class="fa fa-archive mr-2"></span>
                  <span class="font-weight-700"
                    >Resumen de servicios a renovar</span
                  >
                </h3>
              </div>

              <div class="table-responsive">
                <table class="table mb-0 table-hover">
                  <thead class="thead-light">
                    <tr>
                      <th style="min-width: 320px">Servicio</th>
                      <th>Periodicidad</th>
                      <th>Vencimiento</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr v-for="s in datos.servicios" :key="s.id">
                      <td>
                        <div class="d-flex align-items-center">
                          <div class="w-100">
                            <div class="font-weight-700 text-left">
                              {{ s.orden.detalle.servicio.req_normativo }}
                            </div>
                            <div class="text-muted small text-left">
                              {{ s.orden.detalle.norma.nombre }}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td>
                        <span
                          class="badge badge-pill badge-light border px-2 py-1"
                        >
                          {{ s.orden.detalle.servicio.periodicidad }}
                        </span>
                      </td>

                      <td>
                        <div
                          class="d-flex align-items-center justify-content-center flex-wrap"
                        >
                          <div class="font-weight-600 mr-3">
                            {{ formatDate(s.fecha_vencimiento) }}
                          </div>

                          <span
                            v-if="countdownDays(s.fecha_vencimiento) !== null"
                            class="badge badge-pill px-2 py-1"
                            :class="
                              countdownBadgeClass(
                                countdownDays(s.fecha_vencimiento),
                              )
                            "
                          >
                            <span
                              class="dot mr-1"
                              :class="
                                countdownDotClass(
                                  countdownDays(s.fecha_vencimiento),
                                )
                              "
                            ></span>
                            <span class="small font-weight-700"
                              >{{
                                countdownDays(s.fecha_vencimiento) <= 0
                                  ? "Vencido"
                                  : countdownDays(s.fecha_vencimiento) + " días"
                              }}
                            </span>
                          </span>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="!datos.servicios.length">
                      <td colspan="3" class="text-center text-muted py-4">
                        No hay servicios para renovar.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div
                class="card-footer bg-light d-flex align-items-center justify-content-between"
              >
                <span class="text-muted">
                  Total servicios en esta orden:
                  <span class="font-weight-800">{{
                    datos.servicios.length
                  }}</span>
                </span>
              </div>
            </section>

            <!-- Preferencias de contacto -->
            <section class="card shadow-sm mb-4">
              <div class="card-header d-flex align-items-center">
                <h3 class="h6 mb-0 d-flex align-items-center">
                  <span class="fa fa-comments mr-2"></span>
                  <span class="font-weight-700">Comentarios</span>
                </h3>
              </div>

              <div class="card-body p-4 p-md-5">
                <div>
                  <label class="d-block h6 font-weight-700 mb-1" for="comments"
                    >Comentarios adicionales</label
                  >
                  <p class="text-muted small mb-3">
                    Si tiene alguna solicitud especial, duda o comentario, por
                    favor indíquela aquí.
                  </p>

                  <textarea
                    id="comments"
                    v-model.trim="datos.comentarios"
                    maxlength="250"
                    class="form-control"
                    rows="5"
                    placeholder="Escriba sus comentarios..."
                  />
                </div>
              </div>
            </section>

            <!-- CTA -->
            <section class="card shadow-lg border-0 mb-4">
              <div class="card-body text-center p-4 p-md-5">
                <button
                  class="btn btn-primary btn-lg px-5 cta-btn"
                  type="submit"
                  :disabled="isSubmitting"
                >
                  <span class="fa fa-check-circle mr-2"></span>
                  Confirmar Solicitud
                </button>

                <div
                  class="mt-3 text-muted small d-flex align-items-start justify-content-center"
                >
                  <span
                    class="fa fa-info-circle mr-2"
                    style="font-size: 18px"
                  ></span>
                  <span
                    >Esta acción señala su intención de renovar. Nos
                    comunicaremos para darle seguimiento.</span
                  >
                </div>
              </div>
            </section>
          </div>
        </form>
      </div>
    </main>
  </div>
</template>

<script>
import axiosInstance from "@/services/axiosInstance";

export default {
  name: "ConfirmacionRenovacion",
  data() {
    return {
      isSubmitting: false,
      isLoading: false,
      loadError: "",
      datos: {
        orden: {
          cotizacion_renovacion: {
            cliente: {
              nombre: "",
              contacto1: "",
              correo: "",
              telefono: "",
            },
            folio: "",
          },
        },
        servicios: {},
      },
      comentarios: "",
    };
  },
  async created() {
    const token = this.$route.params.token;
    await this.fetchPrefill(token);
  },
  methods: {
    async fetchPrefill(token) {
      this.isLoading = true;
      this.loadError = "";

      if (!token) {
        this.loadError = "Falta el token para cargar los datos del formulario.";
        this.isLoading = false;
        return;
      }

      try {
        const response = await axiosInstance.get("/renovacion/datos/" + token, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.click != null) {
          this.$router.push({ name: "RenovacionConfirmada" });
          return;
        }
        this.datos = response.data;
      } catch (e) {
        console.error(e);
        this.loadError =
          "No se pudo cargar la información, intenta más tarde o mande un correo a contacto@baessh.com.mx";
      } finally {
        this.isLoading = false;
      }
    },

    formatDate(isoDate) {
      const d = this.parseISODate(isoDate);
      if (!d) return "";
      // Formato estilo "15 Oct 2024"
      return d.toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    parseISODate(isoDate) {
      if (!isoDate || typeof isoDate !== "string") return null;
      const [y, m, d] = isoDate.split("-").map((n) => parseInt(n, 10));
      if (!y || !m || !d) return null;
      // Date(y, m-1, d) evita problemas de timezone al parsear
      return new Date(y, m - 1, d);
    },
    countdownDays(isoDate) {
      const due = this.parseISODate(isoDate);
      if (!due) return null;

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());

      const ms = dueDay - today;
      return Math.round(ms / (1000 * 60 * 60 * 24));
    },
    countdownBadgeClass(days) {
      if (days === null) return "badge-light";
      if (days <= 7) return "badge-danger-soft";
      if (days <= 30) return "badge-warning-soft";
      return "badge-success-soft";
    },
    countdownDotClass(days) {
      if (days === null) return "dot-muted";
      if (days <= 7) return "dot-danger";
      if (days <= 30) return "dot-warning";
      return "dot-success";
    },
    async submit() {
      try {
        this.isSubmitting = true;

        await axiosInstance.post("/renovacion/cotizar", this.datos);

        this.$router.push({ name: "RenovacionConfirmada" });
      } catch (e) {
        console.error(e);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
/* Tipografía */
.renewal-page {
  --ink-900: #0b1730;
  --ink-700: #1a2b4a;
  --ink-500: #3b4b66;
  --surface: #ffffff;
  --surface-muted: #f4f6fb;
  --brand: #0f2a5f;
  --brand-2: #1c4aa8;
  --accent: #18a07a;
  font-family: "Inter", system-ui, -apple-system, Segoe UI, Roboto, Helvetica,
    Arial, "Apple Color Emoji", "Segoe UI Emoji";
  background: #ffffff;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

/* Title */
.display-title {
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--ink-900);
}

/* Banner cliente */
.client-banner {
  background: linear-gradient(
    135deg,
    rgba(28, 74, 168, 0.08),
    rgba(24, 160, 122, 0.06)
  );
  border: 1px solid rgba(15, 42, 95, 0.14);
}

/* Iconos en tabla */
.service-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.icon-indigo {
  background: rgba(99, 102, 241, 0.12);
  color: #0e2043;
}
.icon-cyan {
  background: rgba(6, 182, 212, 0.12);
  color: #0b7285;
}
.icon-emerald {
  background: rgba(16, 185, 129, 0.12);
  color: #0f766e;
}

.font-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
}

/* Cards */
.card {
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 9px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.06);
  background: var(--surface);
  position: relative;
  z-index: 1;
  animation: cardIn 0.55s ease both;
}
.card:nth-of-type(1) {
  animation-delay: 0.05s;
}
.card:nth-of-type(2) {
  animation-delay: 0.12s;
}
.card:nth-of-type(3) {
  animation-delay: 0.19s;
}
.card:nth-of-type(4) {
  animation-delay: 0.26s;
}
.card-header {
  border-radius: 9px 9px 0 0;
  background: linear-gradient(135deg, var(--brand), var(--brand-2));
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  color: white;
}
.card-footer {
  background: transparent !important;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
}

/* Inputs */
.form-control {
  border-radius: 12px;
  border: 1px solid rgba(15, 23, 42, 0.14);
  background: #fbfcfe;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.form-control:focus {
  border-color: rgba(28, 74, 168, 0.6);
  box-shadow: 0 0 0 0.2rem rgba(28, 74, 168, 0.12);
}

/* Table */
.table thead th {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.72rem;
  color: var(--ink-500);
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}
.table td {
  border-top: 1px solid rgba(15, 23, 42, 0.06);
}

/* Countdown badge (soft) */
.badge-danger-soft {
  background: rgba(220, 53, 69, 0.08);
  color: #b02a37;
  border: 1px solid rgba(220, 53, 69, 0.18);
}
.badge-warning-soft {
  background: rgba(255, 193, 7, 0.12);
  color: #8a6d00;
  border: 1px solid rgba(255, 193, 7, 0.22);
}
.badge-success-soft {
  background: rgba(40, 167, 69, 0.1);
  color: #1e7e34;
  border: 1px solid rgba(40, 167, 69, 0.2);
}

/* Dot */
.dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  display: inline-block;
  vertical-align: middle;
}
.dot-muted {
  background: #adb5bd;
}
.dot-danger {
  background: #dc3545;
}
.dot-warning {
  background: #ffc107;
}
.dot-success {
  background: #28a745;
}

/* Preferencias (radio cards) */
.choice-card {
  cursor: pointer;
  margin-bottom: 0;
}
.choice-card input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.choice-card-body {
  border: 1px solid rgba(15, 23, 42, 0.14);
  border-radius: 14px;
  padding: 18px 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: box-shadow 0.15s ease, border-color 0.15s ease,
    background 0.15s ease;
  background: #ffffff;
}
.choice-card:hover .choice-card-body {
  box-shadow: 0 10px 18px rgba(0, 0, 0, 0.06);
}
.choice-card input:checked + .choice-card-body {
  border-color: rgba(28, 74, 168, 0.6);
  background: rgba(28, 74, 168, 0.08);
  color: var(--ink-900);
}

/* Botón CTA */
.btn-primary {
  background: linear-gradient(135deg, var(--brand), var(--brand-2));
  border-color: transparent;
}
.btn-primary:hover {
  background: linear-gradient(135deg, #0b224c, #163f93);
  border-color: transparent;
}
.cta-btn {
  box-shadow: 0 18px 32px rgba(15, 42, 95, 0.22);
  transition: transform 0.08s ease;
}
.cta-btn:active {
  transform: scale(0.98);
}

@keyframes cardIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
